import { FormattedMessage } from "react-intl";

import { Button } from "@/components/atoms/Button";
import { Text } from "@/components/atoms/Text";
import AccountFilled from "@/icons/__generated__/AccountFilled/AccountFilled20";
import SquareFoot from "@/icons/__generated__/SquareFootFilled/SquareFootFilled20"; // Adjust the import paths as necessary
import StarFilled from "@/icons/__generated__/StarFilled/StarFilled20";
import TimeFilled from "@/icons/__generated__/TimeFilled/TimeFilled20";
import { cn } from "@/lib/utils";

export const ListingDetailsWithIcons = ({
  spaceReviewStars,
  spaceReviewCount,
  capacity,
  minimum_duration,
  sqft,
  trackReviewsAction,
  isFullServiceVenue,
  attendeesMin = 0,
}: {
  spaceReviewStars: number;
  spaceReviewCount: number;
  capacity: number;
  minimum_duration: string;
  sqft: number;
  trackReviewsAction: (e: React.MouseEvent) => void;
  isFullServiceVenue: boolean;
  attendeesMin: number;
}) => {
  const hasReviews = spaceReviewStars > 0 && spaceReviewCount > 0;
  const hasAttendeesMin = attendeesMin != null && attendeesMin > 0;
  return (
    <div className="lg:tw-w-2/3 tw-w-full lg:tw-pr-16 tw-pt-16 sm:tw-pt-0">
      <div
        className={cn(
          "tw-flex tw-flex-wrap tw-items-center tw-pb-24",
          !isFullServiceVenue && "lg:tw-border-b-[1px] lg:tw-border-gray-300",
        )}
      >
        {hasReviews && (
          <Button
            className="tw-flex tw-items-center tw-justify-start lg:tw-justify-center tw-w-2/4 lg:tw-w-auto tw-mb-16 lg:tw-mb-0"
            variant="underline-m"
            onClick={(e) => {
              trackReviewsAction(e as React.MouseEvent<HTMLButtonElement>);
            }}
          >
            <StarFilled />
            <Text className="tw-ml-4" typeface="body-2-underline">
              {spaceReviewStars.toFixed(1)} ({spaceReviewCount})
            </Text>
          </Button>
        )}
        {hasReviews && (capacity > 0 || !!minimum_duration || sqft > 0) && (
          <div className="tw-hidden lg:tw-block tw-text-gray-600 tw-mx-12 tw--mt-2">
            •
          </div>
        )}

        {capacity > 0 && (
          <div className="tw-flex tw-items-center tw-justify-start lg:tw-justify-center tw-w-2/4 lg:tw-w-auto tw-mb-16 lg:tw-mb-0">
            <AccountFilled />
            <Text className="tw-ml-4" data-testing-id="maximumGuestsLabel">
              {isFullServiceVenue ? (
                hasAttendeesMin ? (
                  <FormattedMessage
                    defaultMessage="{capacity} people"
                    values={{ capacity: `${attendeesMin}–${capacity}` }}
                  />
                ) : (
                  <FormattedMessage
                    defaultMessage="Up to {capacity} people"
                    values={{ capacity }}
                  />
                )
              ) : (
                <FormattedMessage
                  defaultMessage="{capacity} people"
                  values={{ capacity: `${capacity}` }}
                />
              )}
            </Text>
          </div>
        )}

        {(hasReviews || capacity > 0) && (!!minimum_duration || sqft > 0) && (
          <div className="tw-hidden lg:tw-block tw-text-gray-600 tw-mx-12 tw--mt-2">
            •
          </div>
        )}

        {!!minimum_duration && (
          <div className="tw-flex tw-items-center tw-justify-start lg:tw-justify-center tw-w-2/4 lg:tw-w-auto tw-mb-16 lg:tw-mb-0">
            <TimeFilled />
            <Text className="tw-ml-4" data-testing-id="minimumHoursLabel">
              <FormattedMessage
                id="listing.book_listing.minimum_duration_text_shortened"
                defaultMessage="{minimum_duration} hrs min"
                values={{ minimum_duration }}
              />
            </Text>
          </div>
        )}

        {(hasReviews || capacity > 0 || !!minimum_duration) && sqft > 0 && (
          <div className="tw-hidden lg:tw-block tw-text-gray-600 tw-mx-12 tw--mt-2">
            •
          </div>
        )}
        {/* Anchor point for scroll to view for price details */}
        <div id="listing-details-scroll-anchor" />
        {sqft > 0 && (
          <div className="tw-flex tw-items-center tw-justify-start lg:tw-justify-center tw-w-2/4 lg:tw-w-auto tw-mb-16 lg:tw-mb-0">
            <SquareFoot />
            <Text className="tw-ml-4" data-testing-id="areaSizeLabel">
              <FormattedMessage
                id="listing.highlight_summary.sqft_text_and_number"
                defaultMessage="{sqft} sqft"
                values={{ sqft }}
              />
            </Text>
          </div>
        )}
      </div>
    </div>
  );
};
